import { useEffect } from 'react';

const SITE_ORIGIN = 'https://klexu.com';

const upsertMeta = (selector: string, attr: 'name' | 'property', key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute('content');
  el.setAttribute('content', content);
  return prev;
};

export const useDocumentTitle = (title: string, description?: string) => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const path = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';
    const canonicalHref = `${SITE_ORIGIN}${path}`;

    // Canonical link — self-referential per route
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    let prevCanonical: string | null = null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    } else {
      prevCanonical = canonical.getAttribute('href');
    }
    canonical.setAttribute('href', canonicalHref);

    // Per-route Open Graph + Twitter title/url (always) and description (when provided)
    const prevOgTitle = upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
    const prevOgUrl = upsertMeta('meta[property="og:url"]', 'property', 'og:url', canonicalHref);
    const prevTwTitle = upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);

    let prevDesc: string | null = null;
    let prevOgDesc: string | null = null;
    let prevTwDesc: string | null = null;
    if (description) {
      prevDesc = upsertMeta('meta[name="description"]', 'name', 'description', description);
      prevOgDesc = upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
      prevTwDesc = upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    }

    return () => {
      document.title = previousTitle;
      if (canonical && prevCanonical !== null) canonical.setAttribute('href', prevCanonical);
      if (prevOgTitle !== null) upsertMeta('meta[property="og:title"]', 'property', 'og:title', prevOgTitle);
      if (prevOgUrl !== null) upsertMeta('meta[property="og:url"]', 'property', 'og:url', prevOgUrl);
      if (prevTwTitle !== null) upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', prevTwTitle);
      if (description) {
        if (prevDesc !== null) upsertMeta('meta[name="description"]', 'name', 'description', prevDesc);
        if (prevOgDesc !== null) upsertMeta('meta[property="og:description"]', 'property', 'og:description', prevOgDesc);
        if (prevTwDesc !== null) upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', prevTwDesc);
      }
    };
  }, [title, description]);
};